import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'

// Create axios instance with default configuration
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  timeout: 300000, // 5 minutes - allow time for first-time ticker calculations
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage or other storage
    const token = localStorage.getItem('access_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Handle specific error status codes
      switch (error.response.status) {
        case 401:
          // Unauthorized - log but don't redirect automatically
          // The auth context will handle session expiration properly
          console.error('API 401 Unauthorized:', error.config?.url)
          // Only redirect if we're not already on auth-related pages
          if (!window.location.pathname.includes('/auth') &&
              !window.location.pathname.includes('/login')) {
            // Don't redirect immediately - let the component handle it
            // localStorage.removeItem('access_token')
          }
          break
        case 403:
          // Forbidden - user doesn't have permission
          console.error('Access forbidden:', error.response.data)
          break
        case 404:
          // Not found
          console.error('Resource not found:', error.response.data)
          break
        case 500:
          // Server error
          console.error('Server error:', error.response.data)
          break
        default:
          console.error('API error:', error.response.data)
      }
    } else if (error.request) {
      // Network error - no response received
      console.error('Network error:', error.message)
    } else {
      // Error setting up request
      console.error('Request error:', error.message)
    }
    return Promise.reject(error)
  }
)

export default api

// Type-safe API methods
export const apiClient = {
  get: <T>(url: string, params?: object) =>
    api.get<T>(url, { params }).then((res) => res.data),

  post: <T>(url: string, data?: object) =>
    api.post<T>(url, data).then((res) => res.data),

  put: <T>(url: string, data?: object) =>
    api.put<T>(url, data).then((res) => res.data),

  patch: <T>(url: string, data?: object) =>
    api.patch<T>(url, data).then((res) => res.data),

  delete: <T>(url: string) =>
    api.delete<T>(url).then((res) => res.data),
}
